import {
  DragEvent,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './Sidebar.scss';

import {
  Box,
  Button,
  CircularProgress,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { BookOpen, Info, Wrench } from '@phosphor-icons/react';
import { useLocation } from 'react-router-dom';
import { useStoreApi } from 'reactflow';
import KitModal from '../components/UI/KitModal';
import { colors } from '../components/UI/Node/nodeConstants';
import { getAPIErrorMessage } from '../helpers/helpers';
import { useLibrariesQuery } from '../queries/libraries';
import { Library } from '../service/LibraryService';
import { useAuthStore } from '../store/storeAuth';
import useFlowsStore from '../store/storeFlows';
import { useNotificationStore } from '../store/storeNotifications';
import { libraryGroups, types } from './sidebarFlowConstants';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

const ApiConfigModal = ({
  show,
  onHide,
  config,
  flowId,
  apiKey,
}: {
  show: boolean;
  onHide: () => void;
  config: string;
  flowId: string;
  apiKey: string;
}) => {
  const checkText = useMemo(() => {
    const text =
      config === 'No var input nodes found'
        ? config
        : `{
        ${config}
      }`;

    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch (e) {
      return config;
    }
  }, [config]);

  const generateLinkForFlowId = useMemo(() => {
    if (window.location.origin.includes('localhost')) {
      return `http://localhost:3001/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('staging')) {
      return `https://api-staging.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('app')) {
      return `https://api.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('dev')) {
      return `https://api-dev.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }
  }, [flowId]);

  const { user } = useAuthStore(state => state);

  return (
    <KitModal maxWidth={'md'} fullWidth show={show} onHide={onHide}>
      <Box>
        <Typography
          variant={'h5'}
          color={user?.theme === 'dark' ? '#D0D5DD' : '#101828'}
        >
          API Config
        </Typography>

        <br />

        <Typography
          variant={'body1'}
          color={user?.theme === 'dark' ? '#D0D5DD' : '#101828'}
        >
          You can use the following link to access the API for the agent.
        </Typography>
        <br />
        <TextField
          fullWidth
          value={generateLinkForFlowId}
          InputProps={{
            readOnly: true,
            style: {
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            },
          }}
          sx={{
            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            border: `2px solid ${
              user?.theme === 'dark' ? '#475467' : '#EAECF0'
            }`,
          }}
        />

        <br />

        <Typography
          variant={'body1'}
          color={user?.theme === 'dark' ? '#D0D5DD' : '#101828'}
        >
          Use the following API Key to authenticate the API request.
        </Typography>
        <br />

        <TextField
          fullWidth
          value={'api-key' + ' : ' + apiKey}
          InputProps={{
            readOnly: true,
            style: {
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            },
          }}
          sx={{
            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            border: `2px solid ${
              user?.theme === 'dark' ? '#475467' : '#EAECF0'
            }`,
          }}
        />

        <Button
          onClick={() => {
            navigator.clipboard.writeText(generateLinkForFlowId!);
          }}
        >
          Copy Link
        </Button>

        <br />
        <br />

        <Typography
          variant={'body1'}
          color={user?.theme === 'dark' ? '#D0D5DD' : '#101828'}
        >
          This is the API configuration for the agent. You can use this to
          generate the API for the agent.
        </Typography>

        <br />
      </Box>
      <Box>
        <TextField
          multiline
          rows={20}
          fullWidth
          value={checkText}
          InputProps={{
            readOnly: true,
            style: {
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            },
          }}
          sx={{
            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            border: `2px solid ${
              user?.theme === 'dark' ? '#475467' : '#EAECF0'
            }`,
          }}
        />
      </Box>
      <br />
      <Button
        onClick={() => {
          navigator.clipboard.writeText(checkText);
        }}
      >
        Copy API Config Body
      </Button>
    </KitModal>
  );
};

type Props = {
  onSave?: () => void;
  onRestore?: () => void;
  onGenerateApi: string;
  onClickAddNode: (
    data:
      | {
          type: string;
          label: string;
        }
      | any,
  ) => void;
  flowId: string;
  disabled?: boolean;
};

const nameOfSectionBaseInColor = [
  {
    name: 'Text',
    color: colors.text.color,
    Icon: (props: { open: boolean }) => (
      <SvgIcon>
        {props.open ? colors.text.iconFilled : colors.text.icon}
      </SvgIcon>
    ),
    open: false,
    bgColor: 'purple',
  },
  {
    name: 'Visual',
    color: colors.image.color,
    Icon: (props: { open: boolean }) => (
      <SvgIcon>
        {props.open ? colors.image.iconFilled : colors.image.icon}
      </SvgIcon>
    ),
    open: false,
    bgColor: 'blue',
  },

  {
    name: 'Audio',
    color: colors.audio.color,
    Icon: (props: { open: boolean }) => (
      <SvgIcon>
        {props.open ? colors.audio.iconFilled : colors.audio.icon}
      </SvgIcon>
    ),
    open: false,
    bgColor: 'green',
  },
  {
    name: 'Data',
    color: colors.data.color,
    Icon: (props: { open: boolean }) => (
      <SvgIcon>
        {props.open ? colors.data.iconFilled : colors.data.icon}
      </SvgIcon>
    ),
    open: false,
    bgColor: 'orange',
  },
  {
    name: 'Web',
    color: colors.web.color,
    Icon: (props: { open: boolean }) => (
      <SvgIcon>{props.open ? colors.web.iconFilled : colors.web.icon}</SvgIcon>
    ),
    open: false,
    bgColor: 'silver',
  },

  {
    name: 'Functions',
    color: colors.functions.color,
    Icon: (props: { open: boolean }) => (
      <SvgIcon>
        {props.open ? colors.functions.iconFilled : colors.functions.icon}
      </SvgIcon>
    ),
    open: false,
    bgColor: 'red',
  },

  {
    name: 'External ',
    color: colors.external.color,
    Icon: (props: { open: boolean }) => (
      <SvgIcon>
        {props.open ? colors.external.iconFilled : colors.external.icon}
      </SvgIcon>
    ),
    open: false,
    bgColor: 'violet',
  },
  {
    name: 'Library',
    color: colors.library.color,
    Icon: (props: { open: boolean }) => (
      <SvgIcon>
        {props.open ? colors.library.iconFilled : colors.library.icon}
      </SvgIcon>
    ),
    open: false,
    bgColor: 'yellow',
  },
];

const Sidebar = ({
  onGenerateApi,
  onClickAddNode,
  flowId,
  disabled = false,
}: Props) => {
  const [generateApiConfig, setGenerateApiConfig] = useState(false);
  const store = useStoreApi();
  const [isHoveringInOpened, setIsHoveringInOpened] = useState(false);
  const [expandedLibraryGroup, setExpandedLibraryGroup] = useState<
    'System' | 'Organization' | 'Space' | null
  >(null);

  const { user } = useAuthStore(state => state);
  const isDarkTheme = user?.theme === 'dark';
  const { spaceId: storeSpaceId } = useFlowsStore(state => state);
  const { setNotification } = useNotificationStore(state => state);
  const location = useLocation();
  const [sidebar, setSidebar] = useState(nameOfSectionBaseInColor);
  const isLibraryOpen = useMemo(() => {
    return sidebar.some(item => item.open && item.bgColor === 'yellow');
  }, [sidebar]);

  const spaceId = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('spaceId') || storeSpaceId || '';
  }, [location.search, storeSpaceId]);

  // Fetch only when the library panel is open or a group is expanded, keeping
  // the previous lazy-load behavior. React Query refetches automatically when
  // a library mutation invalidates ['libraries', ...], so add/delete now
  // reflects without a manual refresh.
  const isLibraryActive = isLibraryOpen || Boolean(expandedLibraryGroup);

  const systemLibrariesQuery = useLibrariesQuery('System', null, {
    enabled: isLibraryActive,
  });
  const organizationLibrariesQuery = useLibrariesQuery(
    user?.selectedCompanyId ? 'Organization' : undefined,
    null,
    { enabled: isLibraryActive },
  );
  const spaceLibrariesQuery = useLibrariesQuery(
    spaceId ? 'Space' : 'Private',
    spaceId,
    { enabled: isLibraryActive },
  );

  const systemLibraries: Library[] = Array.isArray(systemLibrariesQuery.data)
    ? systemLibrariesQuery.data
    : [];
  const organizationLibraries: Library[] = Array.isArray(
    organizationLibrariesQuery.data,
  )
    ? organizationLibrariesQuery.data
    : [];
  const spaceLibraries: Library[] = Array.isArray(spaceLibrariesQuery.data)
    ? spaceLibrariesQuery.data
    : [];

  const loadingLibraries: 'System' | 'Organization' | 'Space' | null =
    expandedLibraryGroup === 'System' && systemLibrariesQuery.isLoading
      ? 'System'
      : expandedLibraryGroup === 'Organization' &&
          organizationLibrariesQuery.isLoading
        ? 'Organization'
        : expandedLibraryGroup === 'Space' && spaceLibrariesQuery.isLoading
          ? 'Space'
          : null;

  const trackNodeCreation = (method: 'click' | 'drag', nodeType: string) => {
    window.dataLayer = window.dataLayer || [];
    const event = {
      event: 'node_creation',
      method: method || 'unknown',
      nodeType: nodeType || 'unknown',
      userId: user?.id || 'unknown',
      userName: user?.name || 'unknown',
    };
    // console.log('Data to be pushed:', event);
    window.dataLayer.push(event);
  };

  const onDragStart = (event: DragEvent<HTMLDivElement>, nodeType: any) => {
    event.dataTransfer.setData(
      'application/reactflow',
      JSON.stringify(nodeType),
    );
    event.dataTransfer.effectAllowed = 'move';
  };

  const ref = useRef<
    HTMLDivElement | HTMLParagraphElement | HTMLHeadingElement | null
  >(null);

  const replacedText =
    onGenerateApi === 'No var input nodes found'
      ? onGenerateApi
      : '"variableInputs":' +
        ' \n' +
        onGenerateApi
          .replace(/,/g, ',\n')
          .replace(/\{/g, '{\n')
          .replace(/\[/g, '[\n')
          .replace(/\}/g, '\n}')
          .replace(/\]/g, '\n]');

  const positionsBasedOnRefAndIndex = useCallback(
    (index: number) => {
      if (ref.current) {
        if (index === 0) {
          return -1;
        }

        const lastIndex = sidebar.length - 1;

        if (index === lastIndex) {
          return index * 75 - 160;
        }
        if (index === 3) {
          return index * 75 - 15;
        }

        if (index === 4) {
          return index * 75 - 20;
        }

        if (index === 5) {
          return index * 75 - 30;
        }

        return index * 75;
      }
    },
    [ref.current],
  );
  const open = sidebar?.findIndex((item, index) => item.open);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (!isHoveringInOpened && !expandedLibraryGroup) {
      timeoutId = setTimeout(() => {
        setSidebar(prev => prev.map(item => ({ ...item, open: false })));
      }, 3000);
    }

    return () => clearTimeout(timeoutId);
  }, [open, isHoveringInOpened, expandedLibraryGroup]);

  useEffect(() => {
    const error =
      systemLibrariesQuery.error ||
      organizationLibrariesQuery.error ||
      spaceLibrariesQuery.error;
    if (!error) return;
    setNotification({
      type: 'error',
      message: getAPIErrorMessage(error),
    });
  }, [
    systemLibrariesQuery.error,
    organizationLibrariesQuery.error,
    spaceLibrariesQuery.error,
    setNotification,
  ]);

  const getLibrariesForGroup = (
    groupTitle: 'System' | 'Organization' | 'Space',
  ): Library[] => {
    if (groupTitle === 'System') return systemLibraries;
    if (groupTitle === 'Organization') return organizationLibraries;
    if (groupTitle === 'Space') return spaceLibraries;
    return [];
  };

  return (
    <>
      <div
        className={` right-sidebar `}
        style={{
          position: 'absolute',
          left: 10,
          top: 30,
          backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#FFFFFF',
          border: user?.theme === 'dark' ? '#344054' : '#D0D5DD',
          borderRadius:
            open === 0
              ? '8px 0 8px 8px'
              : open === sidebar.length - 1
                ? '8px 0 8px 8px'
                : open !== -1
                  ? '8px 0 8px 8px'
                  : '8px',
          ...(disabled && {
            pointerEvents: 'none',
            opacity: 0.4,
          }),
        }}
      >
        <aside>
          <Box ref={ref} display={'flex'} flexDirection={'column'} gap={'16px'}>
            {sidebar.map(({ name, color, Icon, open, bgColor }, index) => (
              <Fragment key={index}>
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  gap={'5px'}
                  alignContent={'center'}
                  alignItems={'center'}
                  onClick={() => {
                    // trackNodeCreation('click', name);
                    setSidebar(prev => {
                      const newSidebar = prev.map((item, idx) => {
                        if (idx === index) {
                          return {
                            ...item,
                            open: !item.open, // Toggling the open state
                          };
                        } else {
                          return {
                            ...item,
                            open: false, // Closing other sections
                          };
                        }
                      });
                      return newSidebar;
                    });
                  }}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: '4px',
                    padding: '4px 0',
                    // backgroundColor: bgColor,
                    backgroundColor: open
                      ? user?.theme === 'dark'
                        ? '#1D2939'
                        : '#FFFFFF'
                      : user?.theme === 'dark'
                        ? '#1D2939'
                        : '#FFFFFF',

                    '&:hover': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#101828' : '#F2F4F7',
                    },
                  }}
                >
                  <Icon open={open} />

                  <Typography
                    sx={{
                      fontWeight: 400,
                      color: open
                        ? color
                        : user?.theme === 'dark'
                          ? '#D0D5DD'
                          : '#667085',
                      fontSize: '14px',
                    }}
                  >
                    {name}
                  </Typography>
                </Box>
              </Fragment>
            ))}
          </Box>
        </aside>
        <Box
          sx={{
            position: 'absolute',
            left: '77px',
            height: 'fit-content',
            top: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isLibraryOpen ? (
              <Box>
                {libraryGroups
                  .filter(group => {
                    const libraries = getLibrariesForGroup(group.title);
                    return libraries.filter(library => library.flow).length > 0;
                  })
                  .map(group => (
                    <Box key={group.title} sx={{ mb: '4px' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          border:
                            user?.theme === 'dark'
                              ? '1px solid #1D2939'
                              : '1px solid #F9FAFB',
                          borderRadius: '0 4px 4px 0',
                          bgcolor:
                            expandedLibraryGroup === group.title
                              ? user?.theme === 'dark'
                                ? '#1D2939'
                                : '#F2F4F7'
                              : user?.theme === 'dark'
                                ? '#101828'
                                : '#fff',
                          padding: '10px 8px',
                          cursor: 'pointer',
                          gap: '4px',
                          width: '200px',
                          minWidth: '200px',
                          maxWidth: '200px',
                          boxSizing: 'border-box',
                          color: user?.theme === 'dark' ? '#D0D5DD' : '#667085',
                          '&:hover': {
                            backgroundColor:
                              user?.theme === 'dark' ? '#1D2939' : '#F2F4F7',
                          },
                        }}
                        onMouseEnter={() => {
                          setIsHoveringInOpened(true);
                        }}
                        onMouseLeave={e => {
                          if (expandedLibraryGroup === group.title) {
                            return;
                          }
                          setIsHoveringInOpened(false);
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          const newExpandedGroup =
                            expandedLibraryGroup === group.title
                              ? null
                              : group.title;
                          setExpandedLibraryGroup(newExpandedGroup);
                          setIsHoveringInOpened(true);
                        }}
                      >
                        <Typography
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {group.title}
                        </Typography>
                        <Box
                          sx={{
                            transform:
                              expandedLibraryGroup === group.title
                                ? 'rotate(90deg)'
                                : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                            fontSize: '28px',
                            lineHeight: 1,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          ›
                        </Box>
                      </Box>

                      {expandedLibraryGroup === group.title && (
                        <Box
                          onMouseEnter={() => setIsHoveringInOpened(true)}
                          onMouseLeave={() => setIsHoveringInOpened(false)}
                          sx={{
                            width: '200px',
                            minWidth: '200px',
                            maxWidth: '200px',
                          }}
                        >
                          <>
                            {loadingLibraries === group.title ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  padding: '16px',
                                }}
                              >
                                <CircularProgress size={20} />
                              </Box>
                            ) : (
                              <>
                                {getLibrariesForGroup(group.title).length ===
                                0 ? (
                                  <Box
                                    sx={{
                                      padding: '12px 20px',
                                      color:
                                        user?.theme === 'dark'
                                          ? '#98A2B3'
                                          : '#667085',
                                      fontSize: '14px',
                                    }}
                                  >
                                    No libraries found
                                  </Box>
                                ) : (
                                  getLibrariesForGroup(group.title)
                                    .filter(library => library.flow)
                                    .map((library, idx) => (
                                      <Box
                                        key={`${group.title}-${library.id}-${idx}`}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          border:
                                            user?.theme === 'dark'
                                              ? '1px solid #1D2939'
                                              : '1px solid #F9FAFB',
                                          borderRadius: '0 4px 4px 0',
                                          bgcolor:
                                            user?.theme === 'dark'
                                              ? '#101828'
                                              : '#fff',
                                          padding: '8px 8px 8px 20px',
                                          cursor: 'grab',
                                          gap: '4px',
                                          width: '200px',
                                          minWidth: '200px',
                                          maxWidth: '200px',
                                          boxSizing: 'border-box',
                                          color:
                                            user?.theme === 'dark'
                                              ? '#D0D5DD'
                                              : '#667085',
                                          '&:hover': {
                                            backgroundColor:
                                              user?.theme === 'dark'
                                                ? '#1D2939'
                                                : '#F2F4F7',
                                          },
                                        }}
                                        onClick={() => {
                                          if (library.flow) {
                                            const isPrivate =
                                              library.type === 'space' &&
                                              library.space_id === null;
                                            const libraryScope = isPrivate
                                              ? 'Private'
                                              : library.type
                                                  .charAt(0)
                                                  .toUpperCase() +
                                                library.type.slice(1);
                                            trackNodeCreation(
                                              'click',
                                              `library-${
                                                isPrivate
                                                  ? 'private'
                                                  : library.type
                                              }`,
                                            );
                                            onClickAddNode({
                                              type: 'libraryNode',
                                              label:
                                                library.flow.name ||
                                                'Library Item',
                                              store: store.getState(),
                                              libraryId: library.id,
                                              flowId: library.flow.id,
                                              libraryImageUrl:
                                                library.image_url || null,
                                              libraryScope: libraryScope,
                                            });
                                          }
                                        }}
                                        onMouseEnter={() =>
                                          setIsHoveringInOpened(true)
                                        }
                                        onMouseLeave={() =>
                                          setIsHoveringInOpened(false)
                                        }
                                        onDragStart={event => {
                                          if (library.flow) {
                                            const isPrivate =
                                              library.type === 'space' &&
                                              library.space_id === null;
                                            const libraryScope = isPrivate
                                              ? 'Private'
                                              : library.type
                                                  .charAt(0)
                                                  .toUpperCase() +
                                                library.type.slice(1);
                                            trackNodeCreation(
                                              'drag',
                                              `library-${
                                                isPrivate
                                                  ? 'private'
                                                  : library.type
                                              }`,
                                            );
                                            onDragStart(event, {
                                              type: 'libraryNode',
                                              label:
                                                library.flow.name ||
                                                'Library Item',
                                              libraryId: library.id,
                                              flowId: library.flow.id,
                                              libraryImageUrl:
                                                library.image_url || null,
                                              libraryScope: libraryScope,
                                            });
                                          }
                                        }}
                                        draggable={!!library.flow}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            flex: 1,
                                            minWidth: 0,
                                          }}
                                        >
                                          {library.image_url ? (
                                            <Box
                                              component='img'
                                              src={library.image_url}
                                              alt={library.flow?.name}
                                              sx={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: '4px',
                                                objectFit: 'cover',
                                              }}
                                            />
                                          ) : (
                                            <BookOpen
                                              size={18}
                                              color={
                                                user?.theme === 'dark'
                                                  ? '#D0D5DD'
                                                  : '#667085'
                                              }
                                            />
                                          )}
                                          <Typography
                                            style={{
                                              whiteSpace: 'nowrap',
                                              fontSize: '16px',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              flex: 1,
                                              minWidth: 0,
                                            }}
                                          >
                                            {library.flow?.name ||
                                              'Unnamed Library Item'}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    ))
                                )}
                              </>
                            )}
                          </>
                        </Box>
                      )}
                    </Box>
                  ))}
              </Box>
            ) : (
              <>
                {types.map(
                  ({ label, type, btn, bgColor, tooltip, icon }, index) => {
                    const open = sidebar?.filter((item, index) => item.open);

                    const findOpenWithBgColor = open?.find(
                      item => item.bgColor === bgColor,
                    );

                    if (
                      findOpenWithBgColor?.bgColor === bgColor &&
                      findOpenWithBgColor.open
                    ) {
                      if (label === 'Api Config') {
                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              border:
                                user?.theme === 'dark'
                                  ? '1px solid #1D2939'
                                  : '1px solid #F9FAFB',
                              borderRadius: '0 4px 4px 0',
                              bgcolor:
                                user?.theme === 'dark' ? '#101828' : '#fff',
                              padding: '8px',
                              cursor: 'grab',
                              gap: '4px',
                              color:
                                user?.theme === 'dark' ? '#D0D5DD' : '#667085',
                              '&:hover': {
                                backgroundColor:
                                  user?.theme === 'dark'
                                    ? '#1D2939'
                                    : '#F2F4F7',
                              },
                            }}
                            onMouseEnter={() => {
                              setIsHoveringInOpened(true);
                            }}
                            onMouseLeave={() => {
                              setIsHoveringInOpened(false);
                            }}
                            key={index}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <Wrench
                                color={
                                  user?.theme === 'dark' ? '#D0D5DD' : '#667085'
                                }
                              />
                              <Typography
                                onClick={() => setGenerateApiConfig(true)}
                                style={{
                                  whiteSpace: 'nowrap',
                                  fontSize: '16px',
                                  color:
                                    user?.theme === 'dark'
                                      ? '#D0D5DD'
                                      : '#667085',
                                }}
                              >
                                {btn}
                              </Typography>
                            </Box>
                            {tooltip && (
                              <Tooltip
                                key={index}
                                title={tooltip}
                                placement='right'
                              >
                                <Info
                                  size={20}
                                  color={
                                    user?.theme === 'dark'
                                      ? '#D0D5DD'
                                      : '#667085'
                                  }
                                />
                              </Tooltip>
                            )}
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            border:
                              user?.theme === 'dark'
                                ? '1px solid #1D2939'
                                : '1px solid #F9FAFB',
                            borderRadius: '0 4px 4px 0',
                            bgcolor:
                              user?.theme === 'dark' ? '#101828' : '#fff',
                            padding: '8px',
                            cursor: 'grab',
                            gap: '4px',
                            color:
                              user?.theme === 'dark' ? '#D0D5DD' : '#667085',
                            '&:hover': {
                              backgroundColor:
                                user?.theme === 'dark' ? '#1D2939' : '#F2F4F7',
                            },
                          }}
                          key={index}
                          onClick={() => {
                            if (type) {
                              trackNodeCreation('click', type);
                            }
                            onClickAddNode({
                              type,
                              label,
                              store: store.getState(),
                            });
                          }}
                          onMouseEnter={() => {
                            setIsHoveringInOpened(true);
                          }}
                          onMouseLeave={() => {
                            setIsHoveringInOpened(false);
                          }}
                          onDragStart={event => {
                            if (type) {
                              trackNodeCreation('drag', type);
                            }
                            onDragStart(event, { type, label });
                          }}
                          draggable={true}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            {icon ? icon : <Wrench />}
                            <Typography
                              style={{
                                whiteSpace: 'nowrap',
                                fontSize: '16px',
                              }}
                            >
                              {btn}
                            </Typography>
                          </Box>
                          {tooltip && (
                            <Tooltip
                              key={index}
                              title={tooltip}
                              placement='right'
                            >
                              <Info size={20} />
                            </Tooltip>
                          )}
                        </Box>
                      );
                    }
                  },
                )}
              </>
            )}
          </Box>
        </Box>
      </div>
      <ApiConfigModal
        show={generateApiConfig}
        onHide={() => setGenerateApiConfig(false)}
        config={replacedText}
        flowId={flowId}
        apiKey={
          user?.apiKey! || 'Please add your API Key found on the settings page'
        }
      />
    </>
  );
};

export default Sidebar;
